/**
 * What a ship is worth, and therefore what its vault will hold.
 *
 * ── Why the cap is the ship ─────────────────────────────────────────────────
 *
 * A vault that took any amount would make banking a strictly correct move: fly
 * in, deposit everything, and carried linelits — the only money in the game that
 * can be lost — would never be carried. Tying the ceiling to the hull makes the
 * vault a *facility of the ship* rather than an account at a bank, so a bigger
 * ship is worth building for what it protects as well as what it carries.
 *
 * It also puts a floor under losing one. A player back in a starter cannot bank
 * the proceeds of the ship they just lost, because the ship that was securing
 * that balance is gone — see `canDeposit`.
 */
import { itemValue } from './selling.js';
import { COMPONENT_PRICES, JOINT_PRICE, linePrice } from './trader.js';
/**
 * What the hull and everything in it would fetch.
 *
 * Priced at what the parts cost rather than at what a trader would pay for
 * them: this is a statement about the ship's worth, not an offer, and running it
 * through the sell margin would make the vault shrink every time the market
 * moved.
 *
 * Damage is deliberately not counted. A dented wall is still a wall, and a
 * vault that emptied itself because you clipped a station would be a mechanic
 * nobody could plan around.
 */
export function shipValue(hull, cargo = []) {
    let total = 0;
    for (const line of hull.lines)
        total += linePrice(line.length);
    for (const _joint of hull.joints)
        total += JOINT_PRICE;
    for (const part of hull.components)
        total += COMPONENT_PRICES[part.type] ?? 0;
    // The hold counts too — it is aboard, and it is worth something. Mission
    // cargo and anything else unsellable is worth nothing, which `itemValue`
    // already knows.
    for (const item of cargo)
        total += itemValue(item) * item.count;
    return total;
}
/**
 * The most this ship's vault will hold.
 *
 * One for one with the ship's worth. A round number would be easier to explain
 * and would make every hull below the threshold feel identical; this way every
 * line you add raises the ceiling a little.
 */
export function bankingCap(hull, cargo = []) {
    return shipValue(hull, cargo);
}
/**
 * How much more can go in.
 *
 * Never negative. A player whose ship is now worth less than their balance — the
 * case after losing a hull and starting again in a loaner — has no room at all,
 * and is not asked to make up the difference.
 */
export function roomInVault(banked, hull, cargo = []) {
    return Math.max(0, bankingCap(hull, cargo) - banked);
}
/**
 * Whether anything can be deposited at all.
 *
 * False when the balance already meets or exceeds what the ship is worth. This
 * is the rule that bites after a loss: the vault keeps what is in it — banked
 * money survives, which is the whole point of banking it — but it will not take
 * another linelit until there is a ship behind it again.
 *
 * Withdrawal is deliberately never blocked. Money already banked is yours, and a
 * player who cannot get at it because their ship shrank would be stuck with no
 * way to buy their way back out.
 */
export function canDeposit(banked, hull, cargo = []) {
    return roomInVault(banked, hull, cargo) > 0;
}
//# sourceMappingURL=shipValue.js.map